<template>
  <div>
    <exo-table :root="_self">
      <exo-form :root="_self">
        <template v-slot:titleTooltip>
          <exo-help-dialog :color="'black--text'" title="Launch">
            A launch is a means of reaching an orbit. It can be provided by
            launch companies or specified by the user. The Launch object is used
            to generate launch sequences.
          </exo-help-dialog>
        </template>
        <exo-input-name :root="_self" name="name" />
        <exo-input-string :root="_self" name="rocket">
          <exo-help-dialog title="Rocket name">
            Used to classify launches.
          </exo-help-dialog>
        </exo-input-string>
        <exo-input-date-time :root="_self" name="launchDate" />
        <exo-input-ref-notparametric
          :root="_self"
          name="launchOrbit"
          type="orbit"
          componentRoute="/analyticalorbit"
        >
          <exo-help-dialog title="Orbit reference">
            The orbits from the parametric studies can be used, except those
            which are parametric.
          </exo-help-dialog>
        </exo-input-ref-notparametric>
        <exo-input-select :root="_self" name="deploymentLaunchType">
          <exo-help-dialog title="Type of the launch">
            The launch type changes the way of computing costs (launch and
            propulsion costs).
            <ul>
              <li>
                Rideshare: this launch is shared with other satellites. It is
                defined by a mass cost. Launch parameters such as the altitude
                or the RAAN might be imposed by the launch company.
              </li>
              <li>
                Dedicated: the whole rocket is booked to deploy the satellites.
                It is defined by a fixed launch cost. Launch parameters can be
                adapted.
              </li>
            </ul>
          </exo-help-dialog>
        </exo-input-select>
        <exo-input-select :root="_self" name="ascendingNodeDefinitionType">
          <exo-help-dialog title="Ascending node definition">
            The ascending node of the orbit can be defined by its MLTAN (Mean
            Local Time of the Ascending Node) or its RAAN (Right Ascension of
            the Ascending Node).
            <br />
            If the MLTAN is used, the corresponding value of the RAAN will be
            evaluated from it. By default the definition date of the orbit is
            used but it can be overloaded in the mission configuration.
          </exo-help-dialog>
        </exo-input-select>
        <exo-input-double :root="_self" name="raanAtLaunch" />
        <exo-input-double :root="_self" name="ltanAtLaunch" />
        <exo-input-double :root="_self" name="maximumLoad">
          <exo-help-dialog title="Maximum load">
            <ul>
              <li>For dedicated launches, this is the maximum payload mass.</li>
              <li>
                For rideshare launches, this is the remaining mass available.
              </li>
            </ul>
          </exo-help-dialog>
        </exo-input-double>
        <exo-input-double :root="_self" name="launchCost" />
        <exo-input-double :root="_self" name="massCost">
          <exo-help-dialog title="Cost functions">
            <tooltipCosts />
          </exo-help-dialog>
        </exo-input-double>
      </exo-form>
    </exo-table>
    <exo-modal-references :root="_self" :modalContent="modalRef" />
  </div>
</template>

<script>
import formMixin from '../../mixins/forms.js'
import formAnalyticalMixin from '../../mixins/analyticalFoms.js'
import { mapGetters, mapActions } from 'vuex'
import fieldsDescription from '../../tools/fieldsDescription.json'
import dataTools from '../../tools/data.js'
import tooltipCosts from '../../components/Tooltips/tooltipCosts'

export default {
  name: 'DeploymentLaunch',

  mixins: [formMixin, formAnalyticalMixin],

  components: {
    tooltipCosts
  },

  mounted: function () {
    this.refreshReferences()
  },

  data() {
    return {
      tableFields: [
        { value: 'displayedName', text: 'Name', sortable: true },
        { value: 'rocket', text: 'Rocket name', sortable: true },
        { value: 'launchDate', text: 'Launch date', sortable: true },
        { value: 'launchOrbit', text: 'Launch orbit', sortable: true },
        { value: 'deploymentLaunchType', text: 'Launch type', sortable: true },
        {
          value: 'raanAtLaunch',
          text: 'RAAN' + this.getDefaultUnitLabel('raanAtLaunch'),
          sortable: true
        },
        {
          value: 'ltanAtLaunch',
          text: 'MLTAN' + this.getDefaultUnitLabel('ltanAtLaunch'),
          sortable: true
        },
        {
          value: 'maximumLoad',
          text: 'Maximum load' + this.getDefaultUnitLabel('maximumLoad'),
          sortable: true
        },
        {
          value: 'launchCost',
          text: 'Launch cost' + this.getDefaultUnitLabel('launchCost'),
          sortable: true
        },
        {
          value: 'massCost',
          text: 'Mass cost' + this.getDefaultUnitLabel('massCost'),
          sortable: true
        },
        { value: 'actions', text: 'Actions', sortable: false }
      ],

      modalRef: { content: [] },
      showModal: false,

      enumPropertiesList: this.getEnumProperties(),

      orbitsNotParametrics: [],

      inputs: {
        launchDate: this.getDefaultValue('launchDate'),
        rocket: this.getDefaultValue('rocket'),
        deploymentLaunchType: this.getDefaultValue('deploymentLaunchType'),
        ascendingNodeDefinitionType: this.getDefaultValue(
          'ascendingNodeDefinitionType'
        ),
        raanAtLaunch: this.getDefaultValue('raanAtLaunch'),
        ltanAtLaunch: this.getDefaultValue('ltanAtLaunch'),
        launchOrbit: this.getDefaultValue('launchOrbit'),
        maximumLoad: this.getDefaultValue('maximumLoad'),
        launchCost: this.getDefaultValue('launchCost'),
        massCost: this.getDefaultValue('massCost')
      }
    }
  },

  methods: {
    ...mapActions(['setCurrentMission']),

    getCurrentFormTitle() {
      return 'Launch'
    },

    getCurrentFormType() {
      return 'LaunchDefinition'
    },

    refreshReferences() {
      this.refreshOrbits()
    },

    refreshOrbits() {
      let self = this
      this.$http
        .get(this.getAPI('parametricorbitdefinitions'))
        .then((response) => {
          self.orbitsNotParametrics = response.data
            .filter((obj) => !obj.displayedName.endsWith('*'))
            .map((obj) => ({ value: obj, text: obj.displayedName }))
            .sort((a, b) =>
              a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1
            )
          self.orbitsNotParametricsId = response.data
            .filter((obj) => !obj.displayedName.endsWith('*'))
            .map((obj) => ({ value: obj.id, text: obj.displayedName }))
            .sort((a, b) =>
              a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1
            )
        })
        .catch((e) => {
          self.displayMessage(
            'Error getting list of orbits. ',
            e,
            'AnalyticalMissionErrorDisplay',
            'error'
          )
        })
    },

    getRefList(param) {
      if (param === 'launchOrbit') {
        return this.orbitsNotParametrics
      }
    },

    getRequiredPropertiesList(instance) {
      let objectType = this.getCurrentFormType()
      return dataTools.getPropertiesList(
        fieldsDescription[objectType],
        {
          deploymentLaunchType: instance.deploymentLaunchType,
          ascendingNodeDefinitionType: instance.ascendingNodeDefinitionType
        },
        'required'
      )
    },

    getOptionalPropertiesList(instance) {
      let objectType = this.getCurrentFormType()
      return dataTools.getPropertiesList(
        fieldsDescription[objectType],
        {
          deploymentLaunchType: instance.deploymentLaunchType,
          ascendingNodeDefinitionType: instance.ascendingNodeDefinitionType
        },
        'optional'
      )
    },

    getAllPropertiesUnionList() {
      let objectType = this.getCurrentFormType()
      let requiredList = dataTools.getPropertiesList(
        fieldsDescription[objectType],
        null,
        'required'
      )
      let optionalList = dataTools.getPropertiesList(
        fieldsDescription[objectType],
        null,
        'optional'
      )
      return [...new Set(requiredList.concat(optionalList))]
    }
  },

  computed: {
    ...mapGetters(['currentMission'])
  }
}
</script>
